import React, {useEffect, useCallback, useState, useRef} from "react";
import {useLocation, Link} from "react-router-dom";
import {useAuthUrl} from "../utils/authRedirect";
import { usePostHog } from "@posthog/react";
import {
  Container,
  InputGroup,
  InputGroupText,
  Button,
  ButtonGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Card,
  CardBody,
  Alert,
  Spinner,
} from "reactstrap";
import {AxiosError} from "axios";
import {Tooltip as ReactTooltip} from "react-tooltip";
import requests from "../requests";
import Autocomplete from "./Autocomplete";
import DayPickerInput from "./DayPickerInput";
import {parseDate, isBetweenDatesInclusive} from "../utils/dates";
import {confirmTicker, TickerType} from "../utils/typeValidation";
import {
  WINDOW_LABELS_LONG as WINDOW_LABELS,
  PRODUCTION_FORECAST_WINDOW,
  hybridTargetWindowsParam,
  isAllHybridWindowsParam,
  isHybridMatchWindow,
} from "../utils/windows";
import {processQueryResponse} from "../utils/processQueryResponse";

interface Props {
  tickers: StockTickerData;
  setMatches: React.Dispatch<React.SetStateAction<MatchesState[]>>;
  setQuery: React.Dispatch<React.SetStateAction<StockChartQuery>>;
  setForwardDistribution?: React.Dispatch<React.SetStateAction<ForwardDistribution | null>>;
  setForwardCurve?: React.Dispatch<React.SetStateAction<ForwardCurve | null>>;
  onFetchingChange?: (fetching: boolean) => void;
}

// forecastWindow mirrors PRODUCTION_FORECAST_WINDOW (SLICE_PARAMS' forecast_window in
// daily.py) so an unmodified Matcher query reproduces the nightly pipeline's committed
// horizon. score does NOT mirror production's per-window min_score (80-99): a fresh visit
// with no signal in mind benefits from seeing SOME matches immediately rather than the
// strict bar, which routinely returns nothing for an arbitrary ticker/window — the same
// reasoning SHAPE_DEFAULT_SCORE uses in DrawShapeQuery.tsx (all three of its input modes,
// including a real resampled ticker series), whose value this matches.
// A deep link that wants the production bar (e.g. ViewFeatured reproducing a featured
// signal) passes an explicit `score` URL param, which always overrides this default.
const MATCHER_DEFAULT_SCORE = 35;
const WINDOW_DEFAULTS: Record<number, { score: number; forecastWindow: number }> = {
  10:  { score: MATCHER_DEFAULT_SCORE, forecastWindow: PRODUCTION_FORECAST_WINDOW[10] },
  20:  { score: MATCHER_DEFAULT_SCORE, forecastWindow: PRODUCTION_FORECAST_WINDOW[20] },
  65:  { score: MATCHER_DEFAULT_SCORE, forecastWindow: PRODUCTION_FORECAST_WINDOW[65] },
  130: { score: MATCHER_DEFAULT_SCORE, forecastWindow: PRODUCTION_FORECAST_WINDOW[130] },
  260: { score: MATCHER_DEFAULT_SCORE, forecastWindow: PRODUCTION_FORECAST_WINDOW[260] },
};

export default function QueryMatcher(props: Props) {
  const authUrlFor = useAuthUrl();
  const {tickers, setMatches, setQuery, setForwardDistribution, setForwardCurve, onFetchingChange} = props;
  const [date, setDate] = useState(new Date());
  const [ticker, setTicker] = useState<string>("");
  const [score, setScore] = useState<number>(WINDOW_DEFAULTS[20].score);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxChange, setMaxChange] = useState<number>(100);
  const [fetching, setFetching] = useState(false);
  const [numMatches, setNumMatches] = useState<number>(100);
  const [forecastWindow, setForecastWindow] = useState<number>(WINDOW_DEFAULTS[20].forecastWindow);
  const [windowValue, setWindow] = useState<number>(20);
  const [windowDropdownOpen, setWindowDropdownOpen] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Opt-in because a hybrid request searches three active+delisted table pairs
  // and cross-window score calibration is still an open backend validation item.
  const [hybridMatching, setHybridMatching] = useState(false);
  // On by default so results match the beta-aware featured/track-record and links
  // reproduce; users can turn it off under Advanced.
  const [betaAware, setBetaAware] = useState<boolean>(true);
  // Mirrors the backend's BETA_BAND default (query_utils.py) so an unmodified
  // Matcher query matches the featured/nightly matching process exactly.
  const [betaBand, setBetaBand] = useState<number>(0.5);
  const [tickerTypeFilter, setTickerTypeFilter] = useState<TickerType | 'all'>('all');
  const location = useLocation();
  const posthog = usePostHog();

  const [error, setError] = useState<React.ReactNode | null>(null);
  const [errorColor, setErrorColor] = useState<"danger" | "warning">("danger");
  const [noResults, setNoResults] = useState(false);
  const pendingAutoSubmit = useRef(false);
  const prevUrlTickerRef = useRef<string | null>(null);

  const handleQueryError = useCallback((err: unknown) => {
    if (err instanceof AxiosError) {
      const status = err.response?.status;
      if (status === 401) {
        setErrorColor("danger");
        setError(
          <>
            Please <Link to={authUrlFor("/login")}>log in</Link> to use the Query Matcher.
          </>
        );
      } else if (status === 429) {
        setErrorColor("warning");
        const detail = err.response?.data?.detail as string | undefined;
        posthog.capture("query_rate_limited", { ticker, window: windowValue });
        setError(
          <>
            {detail || "You've reached your daily query limit."}{" "}
            <Link to="/pricing">Upgrade to Pro</Link> for unlimited access.
          </>
        );
      } else if (status === 500) {
        setErrorColor("danger");
        setError("Server error occurred. Please try again later.");
      } else {
        setErrorColor("danger");
        setError(err.response?.data?.error || err.response?.data?.message || "An unexpected error occurred.");
      }
    } else {
      setErrorColor("danger");
      setError("An unexpected error occurred.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog, ticker, windowValue]);

  const onSubmit = useCallback(async () => {
    setError(null);
    setNoResults(false);
    try {
      const urlVal = new URL(window.location.href);
      urlVal.searchParams.set("ticker", ticker);
      urlVal.searchParams.set("window", windowValue.toString());
      const targetWindows = hybridTargetWindowsParam(windowValue, hybridMatching);
      const useHybridMatching = targetWindows !== undefined;
      if (targetWindows) urlVal.searchParams.set("targetWindows", targetWindows);
      else urlVal.searchParams.delete("targetWindows");
      window.history.pushState({}, "", urlVal);

      setFetching(true);
      onFetchingChange?.(true);
      const resp = await requests.get(`/api/query/${ticker}`, {
        params: {
          date,
          numMatches,
          window: windowValue,
          minScore: score,
          forecastWindow,
          minPrice,
          maxChange,
          betaAware: betaAware ? 1 : 0,
          betaBand,
          ...(targetWindows ? {targetWindows} : {}),
        },
      });
      const d = resp.data as APIQueryResponse;
      const {newQuery, newMatches} = processQueryResponse(d);
      setQuery(newQuery);
      setMatches(newMatches);
      setForwardDistribution?.(d.forward_distribution ?? null);
      setForwardCurve?.(d.forward_curve ?? null);
      posthog.capture("pattern_query_submitted", {
        ticker,
        window: windowValue,
        num_matches: newMatches.length,
        min_score: score,
        forecast_window: forecastWindow,
        hybrid_matching: useHybridMatching,
        target_windows: targetWindows,
      });
      if (newMatches.length === 0) {
        setNoResults(true);
        setShowAdvanced(true);
      } else {
        setShowAdvanced(false);
      }
    } catch (error: unknown) {
      console.error("Error on submit:", error);
      handleQueryError(error);
      setMatches([]);
      setQuery({} as StockChartQuery);
      setForwardDistribution?.(null);
      setForwardCurve?.(null);
    } finally {
      setFetching(false);
      onFetchingChange?.(false);
    }
  }, [
    date,
    ticker,
    score,
    numMatches,
    windowValue,
    forecastWindow,
    minPrice,
    maxChange,
    betaAware,
    betaBand,
    hybridMatching,
    setMatches,
    setQuery,
    setForwardDistribution,
    setForwardCurve,
    handleQueryError,
    onFetchingChange,
    posthog,
  ]);

  // On initial page load (no URL ticker), show the top featured result for the
  // current window (same data the homepage shows). Deliberately NOT /api/query:
  // the old behavior cycled through well-known tickers (SPY, MSFT, ...) with
  // default filters — each attempt burned one of a free user's 5 daily queries,
  // and several could yield no matches anyway. /api/featured is cached, free,
  // and quota-exempt; real queries only run when the user clicks Find Matches.
  const autoInitQuery = useCallback(async () => {
    setFetching(true);
    onFetchingChange?.(true);
    setError(null);
    try {
      const featuredResp = await requests.get("/api/featured");
      const featuredData = featuredResp.data as RootStockData;
      const featuredKey = `featured${windowValue}` as "featured10" | "featured20" | "featured65" | "featured130" | "featured260";
      // Top match for the current window; fall back to the 20-day list if empty.
      const topItem = (featuredData[featuredKey]?.up[0] ?? featuredData.featured20?.up[0]) as (StockData & {date: string}) | undefined;
      if (topItem) {
        const t = topItem.query.ticker;
        const d = parseDate((topItem as {date: string}).date);
        setTicker(t);
        setDate(d);
        const urlVal = new URL(window.location.href);
        urlVal.searchParams.set("ticker", t);
        urlVal.searchParams.set("window", windowValue.toString());
        window.history.pushState({}, "", urlVal);
        const {newQuery, newMatches} = processQueryResponse(topItem);
        setQuery(newQuery);
        setMatches(newMatches);
        setForwardDistribution?.(topItem.forward_distribution ?? null);
        setForwardCurve?.(topItem.forward_curve ?? null);
      }
    } catch (error: unknown) {
      handleQueryError(error);
    } finally {
      setFetching(false);
      onFetchingChange?.(false);
    }
  }, [windowValue, setMatches, setQuery, setForwardDistribution, setForwardCurve, onFetchingChange, handleQueryError]); // eslint-disable-line react-hooks/exhaustive-deps

  const dateBounds = (tickerIn: string) => {
    const tickerValue = tickerIn || ticker;
    let dateMin = parseDate("1900-01-01");
    let dateMax = new Date();
    if (
      Object.keys(tickers).length &&
      ticker.length &&
      confirmTicker(ticker) &&
      tickerValue in tickers
    ) {
      dateMin = parseDate(tickers[tickerValue as Ticker]["start"]);
      dateMax = parseDate(tickers[tickerValue as Ticker]["end"]);
    }
    return [dateMin, dateMax];
  };

  const scoreHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setScore(value);
    }
  };

  const priceHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setMinPrice(value);
    }
  };

  const maxChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setMaxChange(value);
    }
  };

  const betaBandHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setBetaBand(value);
    }
  };

  const numMatchesHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setNumMatches(value);
    }
  };

  const forecastWindowHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setForecastWindow(value);
    }
  };

  const validTicker = useCallback(
    (ticker: string): ticker is Ticker => {
      return !!(
        Object.keys(tickers).length &&
        confirmTicker(ticker) &&
        tickers[ticker]
      );
    },
    [tickers],
  );

  const tickerHandler = (tickerValue: string) => {
    if (validTicker(tickerValue)) {
      const [start, end] = dateBounds(tickerValue);
      const boundedDate = parseDate(date > end ? end : date < start ? start : date);
      setDate(boundedDate);
    }
    setTicker(tickerValue);
  };

  let [start, end] = dateBounds(ticker);
  if (!validTicker(ticker)) {
    start = new Date();
    end = new Date();
  } else {
    if (end < date) setDate(end);
  }

  const submitDisabled =
    !validTicker(ticker) || !isBetweenDatesInclusive(date, start, end);

  // Add reset function to clear state when ticker changes
  const resetState = useCallback(() => {
    const defaults = WINDOW_DEFAULTS[20];
    setScore(defaults.score);
    setMinPrice(0);
    setMaxChange(100);
    setBetaBand(0.5);
    setNumMatches(100);
    setForecastWindow(defaults.forecastWindow);
    setWindow(20);
    setHybridMatching(false);
  }, []);

  // On initial load with no ticker in the URL at all (bare /dashboard), use the
  // free/cached autoInitQuery instead of burning a real query. Any URL that
  // names an explicit ticker — even a common one like AAPL or SPY — is a real
  // request for that ticker and must go through onSubmit's actual /api/query
  // call below, not be silently swapped for whatever currently tops the
  // Featured leaderboard.
  useEffect(() => {
    if (Object.keys(tickers).length === 0) return;
    const searchParams = new URLSearchParams(location.search);
    const urlTicker = searchParams.get("ticker");
    const urlDate = searchParams.get("date");
    const isDefaultLoad = !urlDate && !urlTicker;
    if (isDefaultLoad && !ticker) {
      autoInitQuery();
    }
  }, [tickers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlTicker = searchParams.get("ticker");
    const urlWindow = searchParams.get("window");
    const urlDate = searchParams.get("date");
    const urlScore = searchParams.get("score");
    const urlNumMatches = searchParams.get("numMatches");
    const urlForecastWindow = searchParams.get("forecastWindow");
    const urlBetaAware = searchParams.get("betaAware");
    const urlBetaBand = searchParams.get("betaBand");
    const urlTargetWindows = searchParams.get("targetWindows");

    // Default loads (no ticker at all in the URL) are handled by autoInitQuery
    // via the tickers effect above — skip pendingAutoSubmit for those. Any URL
    // with an explicit ticker falls through to a real auto-submitted query.
    const isDefaultLoad = !urlDate && !urlTicker;

    // Only reset state if ticker is changing and we're not loading from URL params
    const hasUrlParams = urlWindow || urlDate;

    if (urlTicker && urlTicker !== prevUrlTickerRef.current && !isDefaultLoad) {
      prevUrlTickerRef.current = urlTicker;
      if (!hasUrlParams) {
        resetState();
      }
      setTicker(urlTicker);

      // Auto-submit when ticker is provided via URL params
      pendingAutoSubmit.current = true;
    }

    // Apply URL params after ticker is set
    let requestedWindow: number | null = null;
    if (urlWindow) {
      const windowVal = parseInt(urlWindow, 10);
      if ([10, 20, 65, 130, 260].includes(windowVal)) {
        requestedWindow = windowVal;
        setWindow(windowVal);
        // Apply per-window defaults; explicit urlScore / urlForecastWindow below will override.
        // windowVal is guaranteed to be one of WINDOW_DEFAULTS' keys (validated above).
        const defaults = WINDOW_DEFAULTS[windowVal];
        if (!urlScore) setScore(defaults.score);
        if (!urlForecastWindow) setForecastWindow(defaults.forecastWindow);
      }
    }

    // The v1 UI deliberately represents the backend's flexible target list as
    // one safe "all compatible horizons" switch. Invalid/ineligible URL state
    // degrades to legacy single-window matching instead of sending a request
    // the backend will reject.
    setHybridMatching(
      requestedWindow !== null
      && isHybridMatchWindow(requestedWindow)
      && isAllHybridWindowsParam(urlTargetWindows),
    );

    if (urlDate) {
      const parsedDate = parseDate(urlDate);
      if (!isNaN(parsedDate.getTime())) {
        setDate(parsedDate);
      }
    }

    if (urlScore) {
      const scoreVal = parseInt(urlScore, 10);
      if (!isNaN(scoreVal) && scoreVal >= 0 && scoreVal <= 100) {
        setScore(scoreVal);
      }
    }

    if (urlNumMatches) {
      const nm = parseInt(urlNumMatches, 10);
      if (!isNaN(nm) && nm > 0) {
        setNumMatches(Math.min(nm, 101));
      }
    }

    if (urlForecastWindow) {
      const fw = parseInt(urlForecastWindow, 10);
      if (!isNaN(fw) && fw > 0) {
        setForecastWindow(fw);
      }
    }

    // Pinned by featured/feed links so they reproduce the beta-aware card; defaults to on.
    if (urlBetaAware !== null && urlBetaAware !== "") {
      setBetaAware(!["0", "false", "no", "off"].includes(urlBetaAware.toLowerCase()));
    }

    if (urlBetaBand) {
      const bb = parseFloat(urlBetaBand);
      if (!isNaN(bb) && bb >= 0 && bb <= 5) {
        setBetaBand(bb);
      }
    }
  }, [location.search, resetState]);

  // Handle auto-submit after state is set
  useEffect(() => {
    if (pendingAutoSubmit.current && validTicker(ticker) && !fetching) {
      pendingAutoSubmit.current = false;
      onSubmit();
    }
  }, [ticker, validTicker, fetching, onSubmit]);

  // Don't call onSubmit on every render, as changes to slider and other inputs
  // should not trigger a new API call immediately.
  // Separate effect for API call when ticker changes
  //useEffect(() => {
  //  if (ticker && validTicker(ticker)) {
  //    onSubmit();
  //  }
  //}, [ticker, validTicker, onSubmit]);

  return (
    <Container className="query-matcher-container">
      <div className="query-header">
        <h1 className="page-title">Query Matcher</h1>
        <p>
          Enter a ticker symbol and select a time period (2 weeks, 4 weeks, 3 months,
          6 months, or 1 year). The service will return a set of other tickers and time
          periods where the price action matches. Advanced filters allow you to adjust the
          match score and prices of match candidates.
        </p>
      </div>
      <Card className="query-card">
        <CardBody>
          <div className="mb-3">
            <ButtonGroup size="sm">
              <Button color={tickerTypeFilter === 'all' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'all'} onClick={() => setTickerTypeFilter('all')}>All</Button>
              <Button color={tickerTypeFilter === 'stock' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'stock'} onClick={() => setTickerTypeFilter('stock')}>Stocks/ETFs</Button>
              <Button color={tickerTypeFilter === 'index' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'index'} onClick={() => setTickerTypeFilter('index')}>Indexes</Button>
              <Button color={tickerTypeFilter === 'crypto' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'crypto'} onClick={() => setTickerTypeFilter('crypto')}>Crypto & Forex</Button>
              <Button color={tickerTypeFilter === 'futures' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'futures'} onClick={() => setTickerTypeFilter('futures')}>Futures</Button>
            </ButtonGroup>
          </div>
          <div className="query-main-row">
            <InputGroup className="custom-input-group">
              <InputGroupText
                data-tooltip-id="ticker-tooltip"
                data-tooltip-content="Stock symbol to search for"
              >
                Ticker
              </InputGroupText>
              <ReactTooltip id="ticker-tooltip" />
              <Autocomplete
                data={Object.keys(tickers)}
                onChangeProp={tickerHandler}
                onSelectProp={() => { pendingAutoSubmit.current = true; }}
                value={ticker}
                typeFilter={tickerTypeFilter}
              />
            </InputGroup>

            <InputGroup className="custom-input-group">
              <InputGroupText
                data-tooltip-id="score-tooltip"
                data-tooltip-content="Minimum similarity score (0-100%) for returned matches"
              >
                Min Score
              </InputGroupText>
              <ReactTooltip id="score-tooltip" />
              <input
                className="custom-range-input"
                type="range"
                min="1"
                max="100"
                value={score}
                onChange={scoreHandler}
              />
              <InputGroupText className="range-value">{score}%</InputGroupText>
            </InputGroup>

            <InputGroup className="custom-input-group">
              <InputGroupText
                data-tooltip-id="window-size"
                data-tooltip-content="Number of trading days in the pattern window"
              >
                Horizon
              </InputGroupText>
              <ReactTooltip id="window-size" />
              <Dropdown
                id="window-size"
                isOpen={windowDropdownOpen}
                toggle={() => setWindowDropdownOpen((prevState) => !prevState)}
                className="custom-dropdown"
              >
                <DropdownToggle caret>{WINDOW_LABELS[windowValue] ?? `${windowValue} days`}</DropdownToggle>
                <DropdownMenu>
                  {[10, 20, 65, 130, 260].map((v) => (
                    <DropdownItem
                      key={v}
                      active={v === windowValue}
                      onClick={() => {
                        setWindow(v);
                        if (!isHybridMatchWindow(v)) setHybridMatching(false);
                        // v is always one of WINDOW_DEFAULTS' keys (the list mapped above).
                        const defaults = WINDOW_DEFAULTS[v];
                        setScore(defaults.score);
                        setForecastWindow(defaults.forecastWindow);
                      }}
                    >
                      {WINDOW_LABELS[v] ?? `${v} days`}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </InputGroup>

            <Button
              color="primary"
              className="find-match-btn"
              onClick={onSubmit}
              disabled={submitDisabled}
            >
              {fetching ? (
                <>
                  <Spinner size="sm" />
                  <span>Finding...</span>
                </>
              ) : (
                "Find Matches"
              )}
            </Button>
          </div>

          <button
            className="advanced-toggle"
            onClick={() => setShowAdvanced((prev) => !prev)}
            type="button"
          >
            <span className={`advanced-toggle-icon ${showAdvanced ? "open" : ""}`}>&#9656;</span>
            Advanced Filters
          </button>

          {showAdvanced && (
            <div className="advanced-filters">
              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="hybrid-matching"
                  data-tooltip-content="For 3-month, 6-month, and 1-year patterns, search all three compatible historical horizons together. Cross-horizon scores are experimental and not yet separately calibrated."
                >
                  Cross-Horizon Search
                </InputGroupText>
                <ReactTooltip id="hybrid-matching" />
                <label className={`beta-aware-switch ${!isHybridMatchWindow(windowValue) ? "disabled" : ""}`}>
                  <input
                    type="checkbox"
                    checked={hybridMatching && isHybridMatchWindow(windowValue)}
                    disabled={!isHybridMatchWindow(windowValue)}
                    onChange={(e) => setHybridMatching(e.target.checked)}
                  />
                  <span>
                    {isHybridMatchWindow(windowValue)
                      ? hybridMatching ? "3m + 6m + 1y" : "Off"
                      : "Available for 3m–1y"}
                  </span>
                </label>
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="beta-aware"
                  data-tooltip-content="Only compare against stocks that move as much as this one on a typical day, so the % moves you see are apples-to-apples. On by default."
                >
                  Similar Volatility Only
                </InputGroupText>
                <ReactTooltip id="beta-aware" />
                <label className="beta-aware-switch">
                  <input
                    type="checkbox"
                    checked={betaAware}
                    onChange={(e) => setBetaAware(e.target.checked)}
                  />
                  <span>{betaAware ? "On" : "Off"}</span>
                </label>
              </InputGroup>

              {betaAware && (
                <InputGroup className="custom-input-group">
                  <InputGroupText
                    data-tooltip-id="beta-band"
                    data-tooltip-content="How closely an analog's historical volatility must match this stock's to be preferred - smaller is stricter, larger is looser. Only applies when Similar Volatility Only is on."
                  >
                    Volatility Tolerance
                  </InputGroupText>
                  <ReactTooltip id="beta-band" />
                  <input
                    className="custom-range-input"
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={betaBand}
                    onChange={betaBandHandler}
                  />
                  <InputGroupText className="range-value">&plusmn;{betaBand.toFixed(1)}</InputGroupText>
                </InputGroup>
              )}

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="date-tooltip"
                  data-tooltip-content="The last day of the chart pattern you're matching against"
                >
                  Pattern End Date
                </InputGroupText>
                <ReactTooltip id="date-tooltip" />
                <DayPickerInput
                  date={date}
                  onDateChange={(date) => setDate(date)}
                  start={start}
                  end={end}
                />
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="min-price"
                  data-tooltip-content="Skip analogs where the stock was trading below this price - filters out penny-stock noise"
                >
                  Min Price
                </InputGroupText>
                <ReactTooltip id="min-price" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={minPrice}
                  min="0"
                  max="9999"
                  onChange={priceHandler}
                />
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="max-change"
                  data-tooltip-content="Drop analogs that moved more than this % afterward, so one extreme outlier doesn't skew the average outcome"
                >
                  Exclude Moves Over
                </InputGroupText>
                <ReactTooltip id="max-change" />
                <input
                  className="custom-range-input"
                  type="range"
                  min="0"
                  max="200"
                  value={maxChange}
                  onChange={maxChangeHandler}
                />
                <InputGroupText className="range-value">{maxChange}%</InputGroupText>
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="max-matches"
                  data-tooltip-content="The most similar historical patterns to pull in, ranked by similarity score"
                >
                  Max Matches
                </InputGroupText>
                <ReactTooltip id="max-matches" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={numMatches}
                  min="1"
                  max="101"
                  onChange={numMatchesHandler}
                />
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="forecast-window"
                  data-tooltip-content="How many days after the pattern to measure the outcome - e.g. 5 means 'what happened 5 days later'"
                >
                  Forecast Window (days)
                </InputGroupText>
                <ReactTooltip id="forecast-window" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={forecastWindow}
                  min="1"
                  max="100"
                  onChange={forecastWindowHandler}
                />
              </InputGroup>
            </div>
          )}

          {error && (
            <Alert color={errorColor} className="error-alert">
              {error}
            </Alert>
          )}

          {noResults && !error && (
            <Alert color="warning" className="mt-3 mb-0">
              No matches found for <strong>{ticker}</strong> ({windowValue}-day window, min score: {score}%).
              Try a different security, lower the <strong>Min Score</strong>, select a different <strong>Horizon</strong>, or adjust <strong>Advanced Filters</strong>.
            </Alert>
          )}

        </CardBody>
      </Card>
    </Container>
  );
}
